import {
  Center,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { useFetchAccountsQuery, useFetchAfeDetailsQuery } from 'api';
import { PostInvoiceTransactionAccountRequest } from 'api/bindings/PostInvoiceTransactionAccountRequest';
import { PostInvoiceTransactionRequest } from 'api/bindings/PostInvoiceTransactionRequest';
import { Validators, useAuth } from 'core';
import Decimal from 'decimal.js';
import {
  AppInput,
  CurrencyLabel,
  FieldError,
  FormButtonGroup,
} from 'features/components';
import { every } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { FiEdit, FiPlus } from 'react-icons/fi';
import { InvoiceFormRevisionAccountDrawer } from './InvoiceFormRevisionAccountDrawer';

type Form = PostInvoiceTransactionRequest;
export interface InvoiceFormRevisionDrawerContentProps
  extends Omit<DrawerContentProps, 'onSubmit'> {
  afeId: string;
  // invoiceId: string | undefined;
  disallowedNames: string[] | undefined;
  // invoice: InvoiceView | undefined;
  onSubmit: (form: Form) => void;
  onCancel: () => void;
}

type Row = PostInvoiceTransactionAccountRequest;
interface RowEditState {
  index: number;
  row: PostInvoiceTransactionAccountRequest;
}

export const InvoiceFormRevisionDrawerContent = (
  props: InvoiceFormRevisionDrawerContentProps
) => {
  const {
    afeId,
    // invoiceId,
    disallowedNames = [],
    onSubmit,
    onCancel,
    ...rest
  } = props;
  //  Fetches
  const auth = useAuth();
  const { data: afe } = useFetchAfeDetailsQuery(afeId, { skip: !afeId });
  const { data: accounts } = useFetchAccountsQuery(
    { clientId: auth?.user.clientId ?? '0' },
    { skip: !auth?.user.clientId }
  );

  // State
  const allAccounts = useMemo(
    () => accounts?.items.flatMap((x) => x.subAccounts),
    [accounts?.items]
  );

  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      // invoiceId,
      userId: '0',
      accounts: [],
    },
  });
  const { control, watch, formState, handleSubmit, reset, trigger } = form;
  const [editingRow, setEditingRow] = useState<RowEditState>();

  const accountsFieldArray = useFieldArray<Form, 'accounts', '_id'>({
    control,
    name: 'accounts',
    keyName: '_id',
    rules: {
      minLength: { value: 1, message: 'At least one account is required' },
      validate: {
        allPositive: (v) =>
          every(v, (x: PostInvoiceTransactionAccountRequest) =>
            new Decimal(x.amount).gt(0)
          ) || 'All accounts must be non-zero',
        allCommitmentsSpecified: (v) =>
          every(
            v,
            (x: PostInvoiceTransactionAccountRequest) =>
              x.commitmentId && x.commitmentTypeId
          ) || 'All accounts must specify a commitment',
        allSubAccountsSpecified: (v) =>
          every(
            v,
            (x: PostInvoiceTransactionAccountRequest) => x.subAccountId
          ) || 'All rows must specify a valid account',
      },
    },
  });

  const handleRowSave = (row: Row) => {
    const e = editingRow;
    if (!e) {
      return;
    }

    if (e?.index === -1) {
      accountsFieldArray.append(row);
    } else {
      accountsFieldArray.remove(e.index);
      accountsFieldArray.insert(e.index, row);
    }

    setEditingRow(undefined);
  };

  const revisionName = watch('name');

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DrawerContent {...rest}>
          <DrawerCloseButton />
          <DrawerHeader>Invoice Revision: {revisionName}</DrawerHeader>
          <DrawerBody>
            {editingRow && (
              <InvoiceFormRevisionAccountDrawer
                afeId={afeId}
                isOpen={!!editingRow}
                row={editingRow.row}
                onClose={() => setEditingRow(undefined)}
                onSubmit={handleRowSave}
                size="xl"
              />
            )}
            <Stack gap={[2, 4]}>
              <FormControl id="name">
                <FormLabel>Name:</FormLabel>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: 'A revision name is required',
                    },
                    minLength: {
                      value: 3,
                      message:
                        'Revision name must be between 3 and 256 characters.',
                    },
                    maxLength: {
                      value: 256,
                      message:
                        'Revision name must be between 3 and 256 characters.',
                    },
                    validate: {
                      isNotOneOf: Validators.isNotOneOfStrings(disallowedNames),
                    },
                  }}
                  render={(props) => <AppInput {...props.field} />}
                />
              </FormControl>
              <FormControl id="accounts">
                <Flex py="2">
                  <FormLabel flexGrow={1}>Accounts:</FormLabel>
                  <IconButton
                    aria-label="New Row"
                    icon={
                      // @ts-ignore
                      <FiPlus />
                    }
                    variant="solid"
                    colorScheme="blue"
                    onClick={() =>
                      setEditingRow({
                        row: {
                          subAccountId: '0',
                          amount: '0',
                          commitmentTypeId: 0,
                          commitmentId: '0',
                        },
                        index: -1,
                      })
                    }
                  />
                </Flex>

                <TableContainer>
                  <Table variant={'simple'}>
                    <Thead>
                      <Tr>
                        <Th></Th>
                        <Th>Commitment</Th>
                        <Th>Account</Th>
                        {/* <Th>Existing</Th> */}
                        <Th>Amount</Th>
                        {/* <Th>Net</Th> */}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {!accountsFieldArray.fields.length && (
                        <Tr>
                          <Td colSpan={4}>
                            <Center>No Commitments to Display</Center>
                          </Td>
                        </Tr>
                      )}
                      {accountsFieldArray.fields.map((r, i) => (
                        <Tr key={r._id}>
                          <Td>
                            <IconButton
                              aria-label="Edit Account"
                              icon={
                                // @ts-ignore
                                <FiEdit />
                              }
                              onClick={() =>
                                setEditingRow({
                                  index: i,
                                  row: r,
                                })
                              }
                            />
                          </Td>
                          <Td>
                            {
                              afe?.commitments.find(
                                (x) =>
                                  x.commitmentId === r.commitmentId &&
                                  x.commitmentTypeId === r.commitmentTypeId
                              )?.name
                            }
                          </Td>
                          <Td>
                            {
                              allAccounts?.find(
                                (x) => x.subAccountId === r.subAccountId
                              )?.fullSubAccountName
                            }
                          </Td>
                          {/* <Td>
                            <CurrencyLabel value={r.existing} />
                          </Td> */}
                          <Td>
                            <CurrencyLabel value={r.amount} />
                          </Td>
                          {/* <Td>
                            <CurrencyLabel value={r.net} />
                          </Td> */}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </FormControl>
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <FormButtonGroup
              onCancel={() => {
                reset();
                onCancel();
              }}
              onConfirm={handleSubmit(onSubmit)}
            />
          </DrawerFooter>
        </DrawerContent>
      </form>
    </FormProvider>
  );
};
